/**
 * Table Expansion - Shared helpers and barrel re-exports for table subfolder loaders
 *
 * Each loader function lives in its own file:
 * - loadColumnsFolder.ts
 * - loadKeysFolder.ts
 * - loadConstraintsFolder.ts
 * - loadIndexesFolder.ts
 * - loadTriggersFolder.ts
 * - loadStatisticsFolder.ts
 * - loadPartitionsFolder.ts
 */

import {
  DatabaseExplorerNode,
  DatabaseObjectTreeNode,
  createTableSubfolders,
} from '../../explorer/models/DatabaseTreeModels';
import { useConnectionStore } from '../../connections/store/connectionStore';

/**
 * Get the database engine for a connection (non-hook access)
 * Checks both saved connections and active connections (for browser mode SQLite)
 */
export function getEngineForConnection(connectionId: string): string {
  const state = useConnectionStore.getState();
  // First check saved connections
  const savedConnection = state.connections.find((c) => c.id === connectionId);
  if (savedConnection?.engine) {
    return savedConnection.engine;
  }
  // Then check active connections (for browser mode SQLite connections)
  const activeConnection = state.activeConnections.find((c) => c.id === connectionId);
  if (activeConnection?.connection.engine) {
    return activeConnection.connection.engine;
  }
  return 'mssql';
}

export interface LoadTableSubfoldersOptions {
  node: DatabaseObjectTreeNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
}

/**
 * Loads the standard subfolders for a table node when it's expanded
 */
export function loadTableSubfolders({
  node,
  updateNode,
}: LoadTableSubfoldersOptions) {
  // Don't reload if already has children
  if (node.children && node.children.length > 0) {
    return;
  }

  const { objectDetails } = node.metadata;
  const connectionId = objectDetails.connectionId;
  const database = objectDetails.metadata?.database as string;
  const schema = objectDetails.schema;
  const tableName = objectDetails.name;

  if (!database) {
    return;
  }

  // pgsql tables query stamps `relkind`; 'p' marks a declarative-partitioned
  // parent, which gets an extra Partitions subfolder. Other engines / regular
  // tables leave this undefined, so the folder is never added.
  const isPartitioned = objectDetails.metadata?.relkind === 'p';

  // Create the standard table subfolders
  const subfolders = createTableSubfolders(
    connectionId,
    database,
    schema,
    tableName,
    node.id,
    isPartitioned
  );

  updateNode(node.id, {
    children: subfolders,
    isExpanded: true,
  });
}

// Re-export all loader functions and their option types
export { loadColumnsFolder } from './loadColumnsFolder';
export type { LoadColumnsFolderOptions } from './loadColumnsFolder';

export { loadKeysFolder } from './loadKeysFolder';
export type { LoadKeysFolderOptions } from './loadKeysFolder';

export { loadConstraintsFolder } from './loadConstraintsFolder';
export type { LoadConstraintsFolderOptions } from './loadConstraintsFolder';

export { loadIndexesFolder } from './loadIndexesFolder';
export type { LoadIndexesFolderOptions } from './loadIndexesFolder';

export { loadTriggersFolder } from './loadTriggersFolder';
export type { LoadTriggersFolderOptions } from './loadTriggersFolder';

export { loadStatisticsFolder } from './loadStatisticsFolder';
export type { LoadStatisticsFolderOptions } from './loadStatisticsFolder';

export { loadPartitionsFolder } from './loadPartitionsFolder';
export type { LoadPartitionsFolderOptions } from './loadPartitionsFolder';
